import ReactGA from 'react-ga4';

// Extend the Window interface to include Google Analytics
declare global {
  interface Window {
    gtag: Function;
  }
}

/**
 * Google Analytics service for tracking user interactions
 */
class AnalyticsService {
  private initialized = false;
  private measurementId = 'AW-17057672020';

  /**
   * Initialize Google Analytics
   */
  init = (): void => {
    if (this.initialized) {
      return;
    }

    try {
      // Initialize ReactGA with the Measurement ID
      ReactGA.initialize(this.measurementId, {
        testMode: import.meta.env.DEV,
        gaOptions: {
          debug_mode: import.meta.env.DEV
        }
      });
      
      this.initialized = true;
      console.log('Google Analytics initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Track page views
   * @param path - The current page path
   */
  pageView = (path: string): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      ReactGA.send({ hitType: "pageview", page: path });
      console.log(`Page view tracked: ${path}`);
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track events
   * @param category - Event category
   * @param action - Event action
   * @param label - Event label (optional)
   * @param value - Event value (optional)
   */
  event = (category: string, action: string, label?: string, value?: number): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      ReactGA.event({
        category,
        action,
        label,
        value
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track user account creation
   * @param method - Registration method (email, phone, google)
   * @param userId - User ID (optional)
   */
  trackAccountCreation = (method: 'email' | 'phone' | 'google', userId?: string): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'User',
        action: 'Account Created',
        label: method,
        value: 1
      });

      // Track with gtag for enhanced conversion tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', {
          method: method,
          user_id: userId
        });
      }

      console.log(`Account creation tracked: method=${method}, userId=${userId}`);
    } catch (error) {
      console.error('Failed to track account creation:', error);
    }
  }

  /**
   * Track address creation
   * @param addressType - Type of address (home, work, other)
   * @param isFirstAddress - Whether this is user's first address
   */
  trackAddressCreation = (addressType: string = 'home', isFirstAddress: boolean = false): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Address',
        action: 'Address Created',
        label: addressType,
        value: isFirstAddress ? 1 : 0
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_address', {
          address_type: addressType,
          is_first_address: isFirstAddress
        });
      }

      console.log(`Address creation tracked: type=${addressType}, isFirst=${isFirstAddress}`);
    } catch (error) {
      console.error('Failed to track address creation:', error);
    }
  }

  /**
   * Track subscription/order creation
   * @param subscriptionLevel - Plan level (standard, premium)
   * @param subscriptionType - Billing type (monthly, yearly, trial)
   * @param paymentMethod - Payment method used
   * @param amount - Transaction amount
   * @param currency - Currency used
   * @param subscriptionId - Subscription ID
   */
  trackSubscriptionCreation = (
    subscriptionLevel: 'standard' | 'premium',
    subscriptionType: 'monthly' | 'yearly' | 'trial',
    paymentMethod: string,
    amount: number,
    currency: string = 'THB',
    subscriptionId?: string
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Subscription',
        action: 'Subscription Created',
        label: `${subscriptionLevel}_${subscriptionType}_${paymentMethod}`,
        value: Math.round(amount * 100) // Convert to cents/satang for consistency
      });

      // Track with gtag for enhanced e-commerce tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: subscriptionId,
          value: amount,
          currency: currency.toUpperCase(),
          items: [{
            item_id: `${subscriptionLevel}_${subscriptionType}`,
            item_name: `Piika ${subscriptionLevel} ${subscriptionType} subscription`,
            category: 'Subscription',
            quantity: 1,
            price: amount
          }]
        });

        // Also track as a subscription event
        window.gtag('event', 'begin_checkout', {
          currency: currency.toUpperCase(),
          value: amount,
          subscription_level: subscriptionLevel,
          subscription_type: subscriptionType,
          payment_method: paymentMethod
        });
      }

      console.log(`Subscription creation tracked: ${subscriptionLevel} ${subscriptionType} via ${paymentMethod}, amount=${amount} ${currency}`);
    } catch (error) {
      console.error('Failed to track subscription creation:', error);
    }
  }

  /**
   * Track order completion (for renewals or one-time purchases)
   * @param orderId - Order ID
   * @param orderType - Type of order (renewal, gift, etc.)
   * @param amount - Transaction amount
   * @param currency - Currency used
   * @param paymentMethod - Payment method used
   */
  trackOrderCompletion = (
    orderId: string,
    orderType: 'renewal' | 'gift' | 'one_time',
    amount: number,
    currency: string = 'THB',
    paymentMethod: string
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Order',
        action: 'Order Completed',
        label: `${orderType}_${paymentMethod}`,
        value: Math.round(amount * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: orderId,
          value: amount,
          currency: currency.toUpperCase(),
          order_type: orderType,
          payment_method: paymentMethod
        });
      }

      console.log(`Order completion tracked: ${orderId} (${orderType}) via ${paymentMethod}, amount=${amount} ${currency}`);
    } catch (error) {
      console.error('Failed to track order completion:', error);
    }
  }

  // ========== PURCHASE JOURNEY EVENTS ==========

  /**
   * Track when user views a list of items (e.g., product catalog, search results)
   * @param items - Array of items viewed
   * @param listName - Name of the list (e.g., "Search Results", "Featured Products")
   * @param listId - ID of the list
   */
  trackViewItemList = (
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
      index?: number;
    }>,
    listName?: string,
    listId?: string
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'View Item List',
        label: listName || 'Product List',
        value: items.length
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_item_list', {
          item_list_name: listName,
          item_list_id: listId,
          items: items.map((item, index) => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1,
            index: item.index !== undefined ? item.index : index,
            item_list_name: listName,
            item_list_id: listId
          }))
        });
      }

      console.log(`View item list tracked: ${listName || 'Product List'} with ${items.length} items`);
    } catch (error) {
      console.error('Failed to track view item list:', error);
    }
  }

  /**
   * Track when user selects an item from a list
   * @param item - Selected item details
   * @param listName - Name of the list where item was selected
   * @param listId - ID of the list
   */
  trackSelectItem = (
    item: {
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      index?: number;
    },
    listName?: string,
    listId?: string
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Select Item',
        label: `${item.item_name} from ${listName || 'List'}`,
        value: item.price ? Math.round(item.price * 100) : undefined
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'select_item', {
          item_list_name: listName,
          item_list_id: listId,
          items: [{
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: 1,
            index: item.index,
            item_list_name: listName,
            item_list_id: listId
          }]
        });
      }

      console.log(`Select item tracked: ${item.item_name} from ${listName || 'List'}`);
    } catch (error) {
      console.error('Failed to track select item:', error);
    }
  }

  /**
   * Track when user views item details
   * @param item - Item details
   * @param currency - Currency code
   */
  trackViewItem = (
    item: {
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    },
    currency: string = 'THB'
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'View Item',
        label: item.item_name,
        value: item.price ? Math.round(item.price * 100) : undefined
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_item', {
          currency: currency.toUpperCase(),
          value: item.price,
          items: [{
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }]
        });
      }

      console.log(`View item tracked: ${item.item_name}`);
    } catch (error) {
      console.error('Failed to track view item:', error);
    }
  }

  /**
   * Track when user adds item to cart
   * @param item - Item added to cart
   * @param currency - Currency code
   */
  trackAddToCart = (
    item: {
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    },
    currency: string = 'THB'
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Add to Cart',
        label: item.item_name,
        value: item.price ? Math.round(item.price * (item.quantity || 1) * 100) : undefined
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_to_cart', {
          currency: currency.toUpperCase(),
          value: item.price ? item.price * (item.quantity || 1) : undefined,
          items: [{
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }]
        });
      }

      console.log(`Add to cart tracked: ${item.item_name} (qty: ${item.quantity || 1})`);
    } catch (error) {
      console.error('Failed to track add to cart:', error);
    }
  }

  /**
   * Track when user removes item from cart
   * @param item - Item removed from cart
   * @param currency - Currency code
   */
  trackRemoveFromCart = (
    item: {
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    },
    currency: string = 'THB'
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Remove from Cart',
        label: item.item_name,
        value: item.price ? Math.round(item.price * (item.quantity || 1) * 100) : undefined
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'remove_from_cart', {
          currency: currency.toUpperCase(),
          value: item.price ? item.price * (item.quantity || 1) : undefined,
          items: [{
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }]
        });
      }

      console.log(`Remove from cart tracked: ${item.item_name} (qty: ${item.quantity || 1})`);
    } catch (error) {
      console.error('Failed to track remove from cart:', error);
    }
  }

  /**
   * Track when user views cart
   * @param items - Items in cart
   * @param currency - Currency code
   */
  trackViewCart = (
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    }>,
    currency: string = 'THB'
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      const totalValue = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'View Cart',
        label: `${items.length} items`,
        value: Math.round(totalValue * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_cart', {
          currency: currency.toUpperCase(),
          value: totalValue,
          items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }))
        });
      }

      console.log(`View cart tracked: ${items.length} items, total value: ${totalValue} ${currency}`);
    } catch (error) {
      console.error('Failed to track view cart:', error);
    }
  }

  /**
   * Track when user begins checkout process
   * @param items - Items being checked out
   * @param currency - Currency code
   * @param value - Total value
   */
  trackBeginCheckout = (
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    }>,
    currency: string = 'THB',
    value?: number
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      const totalValue = value || items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Begin Checkout',
        label: `${items.length} items`,
        value: Math.round(totalValue * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'begin_checkout', {
          currency: currency.toUpperCase(),
          value: totalValue,
          items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }))
        });
      }

      console.log(`Begin checkout tracked: ${items.length} items, total value: ${totalValue} ${currency}`);
    } catch (error) {
      console.error('Failed to track begin checkout:', error);
    }
  }

  /**
   * Track when user adds shipping information
   * @param shippingTier - Shipping method selected
   * @param items - Items being shipped
   * @param currency - Currency code
   * @param value - Total value
   */
  trackAddShippingInfo = (
    shippingTier: string,
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    }>,
    currency: string = 'THB',
    value?: number
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      const totalValue = value || items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Add Shipping Info',
        label: shippingTier,
        value: Math.round(totalValue * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_shipping_info', {
          currency: currency.toUpperCase(),
          value: totalValue,
          shipping_tier: shippingTier,
          items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }))
        });
      }

      console.log(`Add shipping info tracked: ${shippingTier}, value: ${totalValue} ${currency}`);
    } catch (error) {
      console.error('Failed to track add shipping info:', error);
    }
  }

  /**
   * Track when user adds payment information
   * @param paymentType - Payment method selected
   * @param items - Items being purchased
   * @param currency - Currency code
   * @param value - Total value
   */
  trackAddPaymentInfo = (
    paymentType: string,
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
    }>,
    currency: string = 'THB',
    value?: number
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      const totalValue = value || items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Add Payment Info',
        label: paymentType,
        value: Math.round(totalValue * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_payment_info', {
          currency: currency.toUpperCase(),
          value: totalValue,
          payment_type: paymentType,
          items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1
          }))
        });
      }

      console.log(`Add payment info tracked: ${paymentType}, value: ${totalValue} ${currency}`);
    } catch (error) {
      console.error('Failed to track add payment info:', error);
    }
  }

  /**
   * Track purchase completion with full transaction details
   * @param transactionId - Unique transaction ID
   * @param items - Items purchased
   * @param currency - Currency code
   * @param value - Total transaction value
   * @param tax - Tax amount
   * @param shipping - Shipping cost
   * @param affiliation - Store affiliation
   * @param coupon - Coupon code used
   */
  trackPurchase = (
    transactionId: string,
    items: Array<{
      item_id: string;
      item_name: string;
      item_category?: string;
      item_brand?: string;
      price?: number;
      quantity?: number;
      coupon?: string;
    }>,
    currency: string = 'THB',
    value?: number,
    tax?: number,
    shipping?: number,
    affiliation?: string,
    coupon?: string
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      const totalValue = value || items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Purchase',
        label: transactionId,
        value: Math.round(totalValue * 100)
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: transactionId,
          value: totalValue,
          currency: currency.toUpperCase(),
          tax: tax,
          shipping: shipping,
          affiliation: affiliation,
          coupon: coupon,
          items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category,
            item_brand: item.item_brand,
            price: item.price,
            quantity: item.quantity || 1,
            coupon: item.coupon
          }))
        });
      }

      console.log(`Purchase tracked: ${transactionId}, value: ${totalValue} ${currency}`);
    } catch (error) {
      console.error('Failed to track purchase:', error);
    }
  }

  /**
   * Track refund (full or partial)
   * @param transactionId - Original transaction ID
   * @param items - Items being refunded (optional for full refund)
   * @param currency - Currency code
   * @param value - Refund amount
   */
  trackRefund = (
    transactionId: string,
    items?: Array<{
      item_id: string;
      item_name?: string;
      quantity?: number;
    }>,
    currency: string = 'THB',
    value?: number
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Ecommerce',
        action: 'Refund',
        label: transactionId,
        value: value ? Math.round(value * 100) : undefined
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        const eventData: any = {
          transaction_id: transactionId,
          currency: currency.toUpperCase()
        };

        if (value !== undefined) {
          eventData.value = value;
        }

        if (items && items.length > 0) {
          eventData.items = items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            quantity: item.quantity || 1
          }));
        }

        window.gtag('event', 'refund', eventData);
      }

      console.log(`Refund tracked: ${transactionId}${items ? ` (${items.length} items)` : ' (full refund)'}`);
    } catch (error) {
      console.error('Failed to track refund:', error);
    }
  }

  /**
   * Track promotion view
   * @param promotions - Promotions viewed
   */
  trackViewPromotion = (
    promotions: Array<{
      promotion_id?: string;
      promotion_name?: string;
      creative_name?: string;
      creative_slot?: string;
      location_id?: string;
    }>
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Promotion',
        action: 'View Promotion',
        label: promotions[0]?.promotion_name || promotions[0]?.promotion_id || 'Unknown',
        value: promotions.length
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_promotion', {
          items: promotions.map(promo => ({
            promotion_id: promo.promotion_id,
            promotion_name: promo.promotion_name,
            creative_name: promo.creative_name,
            creative_slot: promo.creative_slot,
            location_id: promo.location_id
          }))
        });
      }

      console.log(`View promotion tracked: ${promotions.length} promotions`);
    } catch (error) {
      console.error('Failed to track view promotion:', error);
    }
  }

  /**
   * Track promotion selection/click
   * @param promotion - Promotion selected
   */
  trackSelectPromotion = (
    promotion: {
      promotion_id?: string;
      promotion_name?: string;
      creative_name?: string;
      creative_slot?: string;
      location_id?: string;
    }
  ): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Track with ReactGA
      ReactGA.event({
        category: 'Promotion',
        action: 'Select Promotion',
        label: promotion.promotion_name || promotion.promotion_id || 'Unknown',
        value: 1
      });

      // Track with gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'select_promotion', {
          items: [{
            promotion_id: promotion.promotion_id,
            promotion_name: promotion.promotion_name,
            creative_name: promotion.creative_name,
            creative_slot: promotion.creative_slot,
            location_id: promotion.location_id
          }]
        });
      }

      console.log(`Select promotion tracked: ${promotion.promotion_name || promotion.promotion_id}`);
    } catch (error) {
      console.error('Failed to track select promotion:', error);
    }
  }

  /**
   * Track purchase conversion for Google Ads
   * @param transactionId - Optional transaction ID
   * @param url - Optional URL to redirect after tracking
   */
  trackPurchaseConversion = (transactionId?: string, url?: string): void => {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Create a callback function for potential redirect
      const callback = function() {
        if (typeof url !== 'undefined') {
          window.location.href = url;
        }
      };

      // Call the Google Ads conversion tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17057672020/VDGhCLfD9sgaENTW3cU_',
          'transaction_id': transactionId || '',
          'event_callback': callback
        });
        console.log('Purchase conversion tracked', transactionId ? `with ID: ${transactionId}` : '');
      } else {
        console.warn('Google gtag not available for conversion tracking');
        // Execute callback anyway if URL is provided
        if (url) {
          callback();
        }
      }
    } catch (error) {
      console.error('Failed to track purchase conversion:', error);
      // Execute callback anyway if URL is provided
      if (url) {
        window.location.href = url;
      }
    }
  }

  /**
   * Check if Google Analytics is loaded
   */
  isLoaded = (): boolean => {
    return typeof window !== 'undefined' && 
           typeof window.gtag === 'function' && 
           this.initialized;
  }
}

export const analyticsService = new AnalyticsService();

export default analyticsService; 